/** Rejection state passed back from PhotoPicker — see `PhotoPickerRejection`
 * in the package types. Held here so the file is self-contained. */
export interface PhotoRejectionState {
  oversized: boolean;
  total: number;
  wrongType: boolean;
}

export interface PhotoRejectionLines {
  primary: string[];
  subDetails: string[];
}

const INVALID_FILES_HEADER = '{count} images are invalid:';
const OVERSIZED_FILE_MESSAGE = "Files larger than {size} can't be added.";
const UNSUPPORTED_FILE_MESSAGE = 'Only {formats} files can be added.';

export function formatFileSize(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) {
    return `${mb % 1 === 0 ? mb.toFixed(0) : mb.toFixed(1)}MB`;
  }
  const kb = bytes / 1024;
  return `${Math.round(kb)}KB`;
}

export function formatTypeLabel(mimeType: string): string {
  const suffix = mimeType.split('/')[1] ?? mimeType;
  return suffix.toUpperCase();
}

export function buildRequirementsLines(
  acceptedFileTypes?: readonly string[],
  maxFileSizeBytes?: number,
): string[] {
  const lines: string[] = [];
  if (acceptedFileTypes && acceptedFileTypes.length > 0) {
    lines.push(`Supported formats: ${acceptedFileTypes.map(formatTypeLabel).join(', ')}`);
  }
  if (maxFileSizeBytes != null) {
    lines.push(`Maximum file size: ${formatFileSize(maxFileSizeBytes)}`);
  }
  return lines;
}

/**
 * Compose rejection lines from the current rejection state + restriction config.
 *
 * - Single rejection: each per-reason detail is a primary line.
 * - Multi rejection: the "{count} images are invalid:" header is the only
 *   primary line; per-reason details become indented sub-details.
 */
export function buildRejectionLines(
  rejection: PhotoRejectionState,
  acceptedFileTypes?: readonly string[],
  maxFileSizeBytes?: number,
): PhotoRejectionLines {
  const empty: PhotoRejectionLines = { primary: [], subDetails: [] };
  if (rejection.total === 0) {
    return empty;
  }
  const details: string[] = [];
  if (rejection.oversized && maxFileSizeBytes != null) {
    details.push(OVERSIZED_FILE_MESSAGE.replace('{size}', formatFileSize(maxFileSizeBytes)));
  }
  if (rejection.wrongType && acceptedFileTypes && acceptedFileTypes.length > 0) {
    const formats = acceptedFileTypes.map(formatTypeLabel).join(', ');
    details.push(UNSUPPORTED_FILE_MESSAGE.replace('{formats}', formats));
  }
  if (details.length === 0) {
    return empty;
  }
  if (rejection.total === 1) {
    return { primary: details, subDetails: [] };
  }
  return {
    primary: [INVALID_FILES_HEADER.replace('{count}', String(rejection.total))],
    subDetails: details,
  };
}
